import type { AgeRating, AgeRatingAgency } from '@ludolio/shared-ui';

export type { AgeRating, AgeRatingAgency };

// User types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgUsername?: string;
  // 'librarian' is permitted here because librarians may also act as instructors
  // when granted the 'instructor.access' permission.
  role: 'admin' | 'teacher' | 'student' | 'librarian';
  orgId?: string | null;
  permissions?: string[];
  unitIds?: string[];
  createdAt: string;
  needsOnboarding?: boolean;
  needsOrgNoticeConsent?: boolean;
}

// Constant kept in sync with backend PERMISSIONS.INSTRUCTOR_ACCESS.
export const INSTRUCTOR_ACCESS_PERMISSION = 'instructor.access';

export function canActAsInstructor(user: Pick<User, 'role' | 'permissions'> | null | undefined): boolean {
  if (!user) return false;
  if (user.role === 'teacher' || user.role === 'admin') return true;
  return !!user.permissions?.includes(INSTRUCTOR_ACCESS_PERMISSION);
}

// Game types
export interface Game {
  id: string;
  title: string;
  description: string;
  publisher: string;
  version: string;
  status: 'draft' | 'published' | 'archived';
  bannerUrl?: string;
  downloadUrl?: string;
  executablePath: string;
  isPublished: boolean;
  ageRatings?: AgeRating[];
  createdAt: string;
  updatedAt: string;
}

// Achievement types
export interface Achievement {
  id: string;
  gameId: string;
  achievementId: string;
  name: string;
  description: string;
  additionalDescription?: string; // Detailed description for instructors (e.g., branching achievements info)
  icon?: string;
  unlockedIconUrl?: string;
  points: number;
  isSecret: boolean;
  createdAt: string;
}

export interface AchievementWithUnlockCount extends Achievement {
  unlockCount: number;
}

export interface UserAchievement {
  id: string;
  userId: string;
  gameId: string;
  gameTitle?: string;
  achievementId: string;
  unlockedAt: string;
  metadata?: Record<string, any>;
  unitId?: string | null;
  achievement?: Achievement;
  unit?: Pick<Unit, 'id' | 'orgId' | 'code' | 'name' | 'year' | 'semester' | 'isArchived' | 'createdAt' | 'updatedAt'> | null;
}

// Assignment types
export interface Assignment {
  id: string;
  studentId: string;
  gameId: string;
  assignedBy: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  game?: Game;
  student?: User;
}

export type ActivityType = 'quiz' | 'short_answer';
export type ActivityLibraryVisibility = 'private' | 'org' | 'global';

export interface ActivityQuestion {
  question: string;
  options?: string[];
  correctAnswers?: string[];
  multiSelect?: boolean;
}

export interface ActivityConfig {
  questions: ActivityQuestion[];
}

export interface ActivityResponseSummary {
  id: string;
  activityId: string;
  studentId: string;
  unitId?: string;
  response: {
    answers?: Array<{ answer: string }>;
    answer?: string;
  };
  submittedAt: string;
  createdAt?: string;
  student?: {
    id?: string;
    email?: string;
    name?: string;
  };
}

export interface Activity {
  id: string;
  unitId: string;
  gameId: string;
  achievementId: string;
  createdBy: string;
  sourceLibraryItemId?: string | null;
  title: string;
  description?: string | null;
  type: ActivityType;
  config: ActivityConfig;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  game?: Game;
  achievement?: Achievement;
  unit?: Pick<Unit, 'id' | 'orgId' | 'code' | 'name' | 'year' | 'semester' | 'isArchived' | 'createdAt' | 'updatedAt'>;
  creator?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  responses?: ActivityResponseSummary[];
}

export interface ActivityLibraryItem {
  id: string;
  orgId?: string | null;
  gameId: string;
  achievementId: string;
  createdBy: string;
  visibility: ActivityLibraryVisibility;
  title: string;
  description?: string | null;
  type: ActivityType;
  config: ActivityConfig;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  game?: Game;
  achievement?: Achievement;
  creator?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

// Student with achievements summary
export interface StudentWithProgress {
  id: string;
  name: string;
  email: string;
  totalGamesAssigned: number;
  totalAchievements: number;
  assignments: Assignment[];
}

// Game with achievement stats
export interface GameWithStats {
  game: Game;
  totalAchievements: number;
  totalStudents: number;
  averageCompletion: number;
}

export interface TeacherDashboardOverview {
  totalStudents: number;
  totalGames: number;
  totalGamesAssigned: number;
  totalPlayingMinutes: number;
  totalInstallCount: number;
  totalAchievementsUnlocked: number;
}

export interface TeacherAchievementOverview {
  totalStudents: number;
  totalUnlocked: number;
  achievements: AchievementWithUnlockCount[];
}

export interface TeacherGameOverview {
  gameId: string;
  achievements: number;
  uniqueStudentCount: number;
  unitCount: number;
}

// Playing Time types
export interface PlayingTime {
  studentId: string;
  gameId: string;
  totalMinutes: number;
  lastPlayedAt: string;
}

// Install Count types
export interface InstallCount {
  studentId: string;
  gameId: string;
  installCount: number;
}

// Unit types
export interface Unit {
  id: string;
  orgId: string;
  code: string;
  name: string;
  year: number;
  semester: 'semester_1' | 'semester_2' | 'summer' | 'winter';
  isArchived: boolean;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
    region?: string | null;
  };
}

// Unit student with stats (enrolled or pending)
export interface UnitStudent {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: 'admin' | 'teacher' | 'student';
  createdAt: string;
  status: 'enrolled' | 'pending';
  totalPlayingHours: number;
  lastActive: string | null;
}

